// react
import { useCallback, useMemo } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

import { useTheme } from '@emotion/react';

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function useChartHighlightParams({
  paramEnabled = 'hl',
  paramFrom = 'hlFrom',
  paramTo = 'hlTo',
  // Optional: allow multiple plotbands later; for now assume single range.
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  const highlightEnabled = useMemo(() => {
    const v = searchParams.get(paramEnabled);
    return v === '1' || v === 'true';
  }, [searchParams, paramEnabled]);

  const highlightRange = useMemo(() => {
    const from = toInt(searchParams.get(paramFrom));
    const to = toInt(searchParams.get(paramTo));
    if (from == null || to == null) return null;
    if (to <= from) return null;
    return { from, to };
  }, [searchParams, paramFrom, paramTo]);

  const plotBands = useMemo(() => {
    if (!highlightEnabled || !highlightRange) return [];
    return [
      {
        id: 'attention-highlight',
        from: highlightRange.from,
        to: highlightRange.to,
        // keep styling here; avoid hardcoding theme colors if you want
        color: 'rgba(7, 255, 19, 0.25)',
        // label optional
        label: {
          text: '',
          style: {
            fontSize: '12px',
            color: theme.palette.mode === 'dark' ? '#ffffffff' : '#000',
          },
        },
        zIndex: 3,
      },
    ];
  }, [highlightEnabled, highlightRange, theme.palette.mode]);

  const setParams = useCallback(
    (updater, options = { replace: true }) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        updater(next);
        return next;
      }, options);
    },
    [setSearchParams],
  );

  const toggleHighlight = useCallback(() => {
    setParams((next) => {
      const currentlyOn = next.get(paramEnabled) === '1';
      if (currentlyOn) {
        next.delete(paramEnabled);
        next.delete(paramFrom);
        next.delete(paramTo);
      } else {
        next.set(paramEnabled, '1');
        // Do not force from/to here; user will select range on chart
      }
    });
  }, [setParams, paramEnabled, paramFrom, paramTo]);

  const clearHighlight = useCallback(() => {
    setParams((next) => {
      next.delete(paramFrom);
      next.delete(paramTo);
      // keep hl=1 if you want to stay in highlight mode
    });
  }, [setParams, paramFrom, paramTo]);

  /**
   * Highcharts selection handler.
   * - If highlightEnabled: prevent zoom, store selection as plotBand in URL.
   * - Else: allow default zoom.
   */
  const onChartSelection = useCallback(
    (e) => {
      if (!highlightEnabled) return true; // allow zoom

      const min = e?.xAxis?.[0]?.min;
      const max = e?.xAxis?.[0]?.max;

      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
        return false; // prevent zoom anyway while in highlight mode
      }

      // Persist to URL
      setParams((next) => {
        next.set(paramEnabled, '1'); // ensure on
        next.set(paramFrom, String(Math.round(min)));
        next.set(paramTo, String(Math.round(max)));
      });

      return false; // IMPORTANT: prevents the default zoom behavior
    },
    [highlightEnabled, setParams, paramEnabled, paramFrom, paramTo],
  );

  return {
    highlightEnabled,
    highlightRange,
    plotBands,
    toggleHighlight,
    clearHighlight,
    onChartSelection,
  };
}
